import { API_BASE_URL, DEFAULT_COMPANY_ID } from './config';
import { apiFetch } from './apiFetch';

export type AssessmentTemplateDto = {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  version: number;
  isActive: boolean;
  sections: Array<{
    id: string;
    title: string;
    orderIndex: number;
    competencyId?: string;
    config: Record<string, unknown>;
  }>;
};

export type AssessmentTemplateListResponse = {
  success: boolean;
  data?: {
    templates: AssessmentTemplateDto[];
  };
  error?: string;
};

export async function listAssessmentTemplates(
  companyId = DEFAULT_COMPANY_ID,
  includeInactive = false
): Promise<AssessmentTemplateListResponse> {
  const query = new URLSearchParams({
    includeInactive: includeInactive ? 'true' : 'false',
  }).toString();

  const response = await apiFetch(
    `${API_BASE_URL}/api/assessment-templates/company/${companyId}?${query}`,
    {
      method: 'GET',
    }
  );

  return response.json();
}

export async function createAssessmentTemplate(input: {
  companyId?: string;
  name: string;
  description?: string;
}): Promise<AssessmentTemplateListResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/assessment-templates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      name: input.name,
      description: input.description,
    }),
  });

  return response.json();
}

export async function renameAssessmentTemplate(input: {
  templateId: string;
  companyId?: string;
  name: string;
}): Promise<AssessmentTemplateListResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/assessment-templates/${input.templateId}/name`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      name: input.name,
    }),
  });

  return response.json();
}

export async function addAssessmentTemplateSection(
  templateId: string,
  input: {
    companyId?: string;
    title: string;
    orderIndex: number;
    competencyId?: string;
    config?: Record<string, unknown>;
  },
): Promise<{
  success: boolean;
  data?: { section: { id: string; title: string; orderIndex: number; competencyId?: string; config: Record<string, unknown> } };
  error?: string;
}> {
  const response = await apiFetch(`${API_BASE_URL}/api/assessment-templates/${templateId}/sections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      title: input.title,
      orderIndex: input.orderIndex,
      competencyId: input.competencyId,
      config: input.config ?? {},
    }),
  });
  return response.json();
}

export async function setAssessmentTemplateStatus(input: {
  templateId: string;
  companyId?: string;
  isActive: boolean;
}): Promise<AssessmentTemplateListResponse> {
  const response = await apiFetch(`${API_BASE_URL}/api/assessment-templates/${input.templateId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: input.companyId ?? DEFAULT_COMPANY_ID,
      isActive: input.isActive,
    }),
  });

  return response.json();
}

export async function getAssessmentTemplate(
  templateId: string,
  companyId = DEFAULT_COMPANY_ID,
): Promise<{ success: boolean; data?: { template: AssessmentTemplateDto }; error?: string }> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/assessment-templates/${templateId}?companyId=${companyId}`,
    {},
  );
  return response.json();
}

export async function deleteAssessmentTemplateSection(
  templateId: string,
  sectionId: string,
  companyId = DEFAULT_COMPANY_ID,
): Promise<{ success: boolean; data?: { template: AssessmentTemplateDto }; error?: string }> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/assessment-templates/${templateId}/sections/${sectionId}?companyId=${companyId}`,
    { method: 'DELETE' },
  );
  return response.json();
}

export async function reorderAssessmentTemplateSections(
  templateId: string,
  orderedIds: string[],
  companyId = DEFAULT_COMPANY_ID,
): Promise<{ success: boolean; data?: { template: AssessmentTemplateDto }; error?: string }> {
  const response = await apiFetch(
    `${API_BASE_URL}/api/assessment-templates/${templateId}/sections/reorder`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId, orderedIds }),
    },
  );
  return response.json();
}
